import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Rocket, ChevronRight, ChevronDown, CheckCircle2, AlertTriangle, Info,
  Play, Copy, Check, ShieldAlert, Building2, ClipboardList, Loader2,
} from 'lucide-react';

import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import {
  onboardingApi,
  type OnboardingTemplateDto,
  type OnboardingPlanDto,
  type OnboardingResultDto,
  type OnboardingRequest,
  type OnboardingIssue,
} from '@/api/onboarding.api';

type Stage = 'template' | 'company' | 'plan' | 'done';

const STAGES: Array<{ key: Stage; label: string }> = [
  { key: 'template', label: 'Template' },
  { key: 'company',  label: 'Company' },
  { key: 'plan',     label: 'Review plan' },
  { key: 'done',     label: 'Result' },
];

const SEVERITY_STYLE: Record<OnboardingIssue['severity'], string> = {
  BLOCKER: 'border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-800/50 dark:bg-rose-900/20 dark:text-rose-200',
  WARN:    'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800/50 dark:bg-amber-900/20 dark:text-amber-200',
  INFO:    'border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-300',
};

const IssueRow = ({ issue }: { issue: OnboardingIssue }) => (
  <div className={cn('flex items-start gap-2 rounded-lg border px-3 py-2 text-sm', SEVERITY_STYLE[issue.severity])}>
    {issue.severity === 'INFO'
      ? <Info size={14} className="flex-shrink-0 mt-0.5" />
      : <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />}
    <span>{issue.message}</span>
  </div>
);

/** One-time secret: shown at provisioning and never retrievable again. */
const SecretRow = ({ label, value }: { label: string; value: string }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* selection + manual copy still works */ }
  };
  return (
    <div className="mb-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-gray-400 mb-1">{label}</p>
      <div className="flex items-center gap-2">
        <code className="flex-1 font-mono text-xs break-all rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-slate-800 dark:text-slate-100">
          {value}
        </code>
        <button onClick={copy}
          className="px-3 py-2 text-sm font-medium rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600 flex items-center gap-1.5 flex-shrink-0">
          {copied ? <><Check size={15} /> Copied</> : <><Copy size={15} /> Copy</>}
        </button>
      </div>
    </div>
  );
};

export const CompanyOnboardingPage = () => {
  const [stage, setStage] = useState<Stage>('template');

  const [templates, setTemplates] = useState<OnboardingTemplateDto[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [templateId, setTemplateId] = useState<string>('');

  const [code, setCode]             = useState('');
  const [name, setName]             = useState('');
  const [currency, setCurrency]     = useState('LKR');
  const [fiscalMonth, setFiscalMonth] = useState(1);
  const [externalRef, setExternalRef] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  // Empty means "admin.<code>": usernames are per company, so a bare "admin" in every company lets
  // a shared password open someone else's. The server refuses one already used elsewhere.
  const [adminUsername, setAdminUsername] = useState('');
  const effectiveAdminUsername = (adminUsername.trim() || `admin.${code.trim().toLowerCase()}`).toLowerCase();

  const [plan, setPlan]         = useState<OnboardingPlanDto | null>(null);
  const [planning, setPlanning] = useState(false);
  const [openStep, setOpenStep] = useState<string | null>(null);

  const [confirmation, setConfirmation] = useState('');
  const [applying, setApplying] = useState(false);
  const [result, setResult]     = useState<OnboardingResultDto | null>(null);
  const [error, setError]       = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try { setTemplates(await onboardingApi.templates()); }
      catch { setError('Could not load templates.'); }
      finally { setLoadingTemplates(false); }
    })();
  }, []);

  const request = useCallback((): OnboardingRequest => ({
    templateId,
    company: {
      code: code.trim().toUpperCase(),
      name: name.trim(),
      baseCurrencyCode: currency.trim().toUpperCase() || 'LKR',
      fiscalYearStartMonth: fiscalMonth,
      adminEmail: adminEmail.trim() || null,
      adminUsername: effectiveAdminUsername,
    },
    externalCompanyRef: externalRef.trim() || null,
  }), [templateId, code, name, currency, fiscalMonth, adminEmail, effectiveAdminUsername, externalRef]);

  const runPreview = async () => {
    setPlanning(true); setError(null); setPlan(null);
    try {
      const p = await onboardingApi.preview(request());
      setPlan(p);
      setStage('plan');
    } catch (e) {
      setError(errorText(e, 'Preview failed.'));
    } finally { setPlanning(false); }
  };

  const blockers = useMemo(
    () => (plan?.issues ?? []).filter(i => i.severity === 'BLOCKER'), [plan]);

  const runApply = async () => {
    setApplying(true); setError(null);
    try {
      const r = await onboardingApi.apply(request(), confirmation.trim());
      setResult(r);
      setStage('done');
    } catch (e) {
      setError(errorText(e, 'Provisioning failed.'));
    } finally { setApplying(false); }
  };

  const restart = () => {
    setStage('template'); setTemplateId(''); setCode(''); setName('');
    setCurrency('LKR'); setFiscalMonth(1); setExternalRef(''); setAdminEmail(''); setAdminUsername('');
    setPlan(null); setResult(null); setConfirmation(''); setError(null);
  };

  const companyValid = code.trim().length >= 2 && name.trim().length >= 2;

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 pt-6 pb-4 border-b border-slate-200 dark:border-slate-700">
        <div className="flex items-start gap-3">
          <Rocket size={22} className="text-signal mt-0.5" />
          <div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-white">Onboard a company</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5 max-w-3xl">
              Provisions a company from an industry template in one run — chart of accounts, financial
              years, numbering, cost centres, account roles, tender and category bindings, and the
              posting rules that turn an external system&rsquo;s business facts into double entry.
            </p>
          </div>
        </div>

        {/* Stage rail */}
        <ol className="flex items-center gap-2 mt-4 text-xs">
          {STAGES.map((s, i) => {
            const idx = STAGES.findIndex(x => x.key === stage);
            const state = i < idx ? 'done' : i === idx ? 'current' : 'todo';
            return (
              <li key={s.key} className="flex items-center gap-2">
                <span className={cn('flex items-center gap-1.5 px-2.5 py-1 rounded-full font-medium',
                  state === 'current' ? 'bg-signal text-white'
                    : state === 'done' ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300'
                    : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400')}>
                  {state === 'done' ? <Check size={12} /> : <span className="font-mono">{i + 1}</span>}
                  {s.label}
                </span>
                {i < STAGES.length - 1 && <ChevronRight size={13} className="text-slate-300 dark:text-slate-600" />}
              </li>
            );
          })}
        </ol>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {error && (
          <div className="max-w-3xl mb-4 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 dark:border-rose-800/50 dark:bg-rose-900/20 px-3 py-2 text-sm text-rose-800 dark:text-rose-200">
            <AlertTriangle size={15} className="flex-shrink-0 mt-0.5" /> {error}
          </div>
        )}

        {/* ── 1. Template ─────────────────────────────────────────────────── */}
        {stage === 'template' && (
          <div className="max-w-3xl">
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1">Choose a template</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
              The template decides the chart of accounts, how revenue and cost are split, and which
              posting rules are installed. Pick the closest fit — everything it creates can be edited afterwards.
            </p>
            {loadingTemplates ? (
              <div className="flex items-center justify-center py-16"><Spinner size="lg" /></div>
            ) : (
              <div className="space-y-2">
                {templates.map(t => (
                  <button key={t.id} onClick={() => { setTemplateId(t.id); setStage('company'); }}
                    className={cn('w-full text-left rounded-xl border p-4 transition-colors',
                      templateId === t.id
                        ? 'border-signal bg-signal/5'
                        : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-signal/50')}>
                    <div className="flex items-center gap-2">
                      <Building2 size={16} className="text-signal" />
                      <span className="font-semibold text-slate-800 dark:text-slate-100">{t.templateName}</span>
                      <code className="text-xs font-mono text-slate-400">{t.templateCode}</code>
                      <ChevronRight size={15} className="ml-auto text-slate-300 dark:text-slate-600" />
                    </div>
                    {t.description && (
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5">{t.description}</p>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── 2. Company ──────────────────────────────────────────────────── */}
        {stage === 'company' && (
          <div className="max-w-2xl">
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3">Company details</h2>
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Company code" hint="Short, permanent. Used everywhere and never changed.">
                  <input value={code} onChange={e => setCode(e.target.value.toUpperCase())}
                    maxLength={20} placeholder="ACME" className={inputCls} />
                </Field>
                <Field label="Company name">
                  <input value={name} onChange={e => setName(e.target.value)}
                    maxLength={200} placeholder="Acme Wholesale (Pvt) Ltd" className={inputCls} />
                </Field>
                <Field label="Base currency">
                  <input value={currency} onChange={e => setCurrency(e.target.value.toUpperCase())}
                    maxLength={3} placeholder="LKR" className={inputCls} />
                </Field>
                <Field label="Fiscal year starts">
                  <select value={fiscalMonth} onChange={e => setFiscalMonth(Number(e.target.value))} className={inputCls}>
                    {['January','February','March','April','May','June','July','August','September','October','November','December']
                      .map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                  </select>
                </Field>
                <Field label="Administrator username" hint="Must not be used in any other company.">
                  <input value={adminUsername} onChange={e => setAdminUsername(e.target.value)}
                    maxLength={100} placeholder={`admin.${code.trim().toLowerCase() || 'acme'}`} className={inputCls} />
                </Field>
                <Field label="Administrator email" hint="Optional. Where the first sign-in details go.">
                  <input value={adminEmail} onChange={e => setAdminEmail(e.target.value)}
                    type="email" placeholder="finance@acme.lk" className={inputCls} />
                </Field>
                <Field label="External system reference" hint="Optional. The POS or WMS company this mirrors.">
                  <input value={externalRef} onChange={e => setExternalRef(e.target.value)}
                    maxLength={60} placeholder="ACME-WMS" className={inputCls} />
                </Field>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-4">
              <button onClick={() => setStage('template')}
                className="px-3.5 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">
                Back
              </button>
              <button onClick={runPreview} disabled={!companyValid || planning}
                className="px-4 py-2 text-sm font-semibold text-white bg-signal hover:bg-signal-light rounded-lg disabled:opacity-40 flex items-center gap-1.5">
                {planning ? <><Spinner size="sm" /> Building plan…</> : <><ClipboardList size={15} /> Preview the plan</>}
              </button>
              <span className="text-xs text-slate-400">Nothing is created yet.</span>
            </div>
          </div>
        )}

        {/* ── 3. Plan ─────────────────────────────────────────────────────── */}
        {stage === 'plan' && plan && (
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 flex-wrap mb-3">
              <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                Plan for <code className="font-mono text-signal">{plan.companyCode}</code>
              </h2>
              <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                {plan.templateCode}
              </span>
              {plan.companyExists && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                  Company already exists — this will top it up, not replace it
                </span>
              )}
              <span className="ml-auto text-xs font-mono text-slate-500 dark:text-gray-400">
                {plan.totalCreate} create · {plan.totalUpdate} update · {plan.totalSkip} skip
              </span>
            </div>

            {plan.issues.length > 0 && (
              <div className="space-y-1.5 mb-4">
                {plan.issues.map((iss, i) => <IssueRow key={i} issue={iss} />)}
              </div>
            )}

            <div className="space-y-2">
              {plan.steps.map(s => {
                const open = openStep === s.stepKey;
                const nothing = s.create + s.update + s.skip === 0;
                return (
                  <div key={s.stepKey} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
                    <button onClick={() => setOpenStep(open ? null : s.stepKey)} disabled={nothing}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50 dark:hover:bg-slate-700/40 disabled:hover:bg-transparent">
                      {nothing
                        ? <span className="w-4" />
                        : open ? <ChevronDown size={15} className="text-slate-400" /> : <ChevronRight size={15} className="text-slate-400" />}
                      <span className="text-sm font-medium text-slate-800 dark:text-slate-100">{s.stepName}</span>
                      <span className="ml-auto text-xs font-mono text-slate-500 dark:text-gray-400">
                        {nothing ? 'nothing to do' : `${s.create} create${s.update ? ` · ${s.update} update` : ''}${s.skip ? ` · ${s.skip} skip` : ''}`}
                      </span>
                    </button>
                    {open && (
                      <div className="border-t border-slate-100 dark:border-slate-700/60 px-4 py-2 max-h-64 overflow-auto">
                        <table className="w-full text-xs">
                          <tbody>
                            {s.items.map((it, i) => (
                              <tr key={i} className="border-b border-slate-50 dark:border-slate-700/40 last:border-0">
                                <td className="py-1 w-16">
                                  <span className={cn('font-semibold',
                                    it.action === 'CREATE' ? 'text-teal-600 dark:text-teal-400'
                                      : it.action === 'UPDATE' ? 'text-blue-600 dark:text-blue-400'
                                      : 'text-slate-400')}>
                                    {it.action}
                                  </span>
                                </td>
                                <td className="py-1 w-24 text-slate-400">{it.kind}</td>
                                <td className="py-1 font-mono text-slate-600 dark:text-slate-300 w-28">{it.key}</td>
                                <td className="py-1 text-slate-700 dark:text-slate-200">{it.description}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Confirm */}
            <div className="mt-5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
              {blockers.length > 0 ? (
                <div className="flex items-start gap-2 text-sm text-rose-700 dark:text-rose-300">
                  <ShieldAlert size={16} className="flex-shrink-0 mt-0.5" />
                  <span>
                    {blockers.length} blocker{blockers.length > 1 ? 's' : ''} must be resolved before this can run.
                    They are listed above.
                  </span>
                </div>
              ) : (
                <>
                  <p className="text-sm text-slate-600 dark:text-slate-300 mb-3">
                    This writes into a company you are not signed in to. Type the company code
                    <code className="mx-1 font-mono font-semibold text-signal">{plan.companyCode}</code>
                    to confirm.
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <input value={confirmation} onChange={e => setConfirmation(e.target.value.toUpperCase())}
                      placeholder={plan.companyCode} className={cn(inputCls, 'w-44 font-mono')} />
                    <button onClick={runApply}
                      disabled={applying || confirmation.trim().toUpperCase() !== plan.companyCode.toUpperCase()}
                      className="px-4 py-2 text-sm font-semibold text-white bg-signal hover:bg-signal-light rounded-lg disabled:opacity-40 flex items-center gap-1.5">
                      {applying ? <><Loader2 size={15} className="animate-spin" /> Provisioning…</> : <><Play size={15} /> Provision company</>}
                    </button>
                  </div>
                </>
              )}
              <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700/60 flex items-center gap-2">
                <button onClick={() => setStage('company')}
                  className="px-3 py-1.5 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">
                  Back
                </button>
                <button onClick={runPreview} disabled={planning}
                  className="px-3 py-1.5 text-sm text-signal hover:underline disabled:opacity-40">
                  Re-check the plan
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── 4. Result ───────────────────────────────────────────────────── */}
        {stage === 'done' && result && (
          <div className="max-w-3xl">
            <div className={cn('flex items-start gap-2 rounded-lg border px-4 py-3 mb-4',
              result.status === 'COMPLETED'
                ? 'border-teal-200 bg-teal-50 text-teal-800 dark:border-teal-800/50 dark:bg-teal-900/20 dark:text-teal-200'
                : SEVERITY_STYLE.WARN)}>
              {result.status === 'COMPLETED'
                ? <CheckCircle2 size={18} className="flex-shrink-0 mt-0.5" />
                : <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />}
              <div>
                <p className="font-semibold">
                  {result.companyCode} — {result.status === 'COMPLETED' ? 'provisioned' : result.status.toLowerCase()}
                </p>
                <p className="text-sm mt-0.5">
                  {result.steps.reduce((n, s) => n + s.created, 0)} objects created across {result.steps.length} steps.
                </p>
              </div>
            </div>

            {(result.integrationApiKey || result.adminPassword) && (
              <div className="rounded-xl border border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-900/15 p-5 mb-4">
                <p className="text-sm font-semibold text-amber-900 dark:text-amber-200 flex items-center gap-1.5 mb-1">
                  <ShieldAlert size={16} /> Copy these now
                </p>
                <p className="text-xs text-amber-800 dark:text-amber-300 mb-3">
                  Shown once and never again. The API key is stored only as a hash; if it is lost it must be
                  revoked and reissued.
                </p>
                {result.adminUsername && result.adminPassword && (
                  <>
                    <SecretRow label="Administrator username" value={result.adminUsername} />
                    <SecretRow label="Temporary password" value={result.adminPassword} />
                  </>
                )}
                {result.integrationApiKey && (
                  <SecretRow label="Integration API key" value={result.integrationApiKey} />
                )}
              </div>
            )}

            {result.manualFollowUps.length > 0 && (
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 mb-4">
                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-2">Still to do by hand</p>
                <ul className="space-y-1.5">
                  {result.manualFollowUps.map((f, i) => (
                    <li key={i} className="text-sm text-slate-600 dark:text-slate-300 flex items-start gap-2">
                      <span className="text-slate-300 dark:text-slate-600 mt-0.5">•</span> {f}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden mb-4">
              <table className="w-full text-sm">
                <tbody>
                  {result.steps.map(s => (
                    <tr key={s.stepKey} className="border-b border-slate-50 dark:border-slate-700/40 last:border-0">
                      <td className="px-4 py-2 w-8">
                        {s.status === 'OK' ? <CheckCircle2 size={14} className="text-teal-500" />
                          : s.status === 'FAILED' ? <AlertTriangle size={14} className="text-rose-500" />
                          : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-2 py-2 text-slate-700 dark:text-slate-200">{s.stepName}</td>
                      <td className="px-4 py-2 text-right font-mono text-xs text-slate-500 dark:text-gray-400">
                        {s.created} created{s.updated ? ` · ${s.updated} updated` : ''}{s.skipped ? ` · ${s.skipped} skipped` : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {result.issues.length > 0 && (
              <div className="space-y-1.5 mb-4">
                {result.issues.map((iss, i) => <IssueRow key={i} issue={iss} />)}
              </div>
            )}

            <button onClick={restart}
              className="px-4 py-2 text-sm font-semibold text-white bg-signal hover:bg-signal-light rounded-lg">
              Onboard another company
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const inputCls =
  'w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 ' +
  'text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-signal';

const Field = ({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
      {label}
    </label>
    {children}
    {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
  </div>
);

/** Surfaces the API's own message — it names the step and the cause far better than a generic one. */
const errorText = (e: unknown, fallback: string): string => {
  const res = (e as { response?: { data?: { message?: string; errors?: Array<{ message: string }> } } })?.response;
  return res?.data?.errors?.[0]?.message ?? res?.data?.message ?? fallback;
};
